import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  ArrowLeft,
  Loader2,
  Search,
  Calendar,
  ChevronRight,
  ChevronDown,
  Check,
  X,
} from "lucide-react";
import TextInputComp from "../../../components/customer/TextInputComp";
import MediaUploadComp from "../../../components/MediaUploadComp";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import CommonButton from "../../../components/CommonButton";

// Hooks & Types
import {
  useAdminAddSingleProductMutation,
  useLazyGetAllBrandsQuery,
  useLazyGetAttributeMaterQuery,
  useLazyGetAttributeMaterValuesQuery,
} from "../../../redux/actions/Admin/ProductAdmin";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import { IAddEditProductSingleRequest } from "../../../types/Request/AdminRequest";
import { IBrandResponse } from "../../../types/Response/AdminResponse";
import { useLazyGetCategoryQuery } from "../../../redux/actions/Admin/dashboardAdmin";
import {
  AttributeMaster,
  AttributeValue,
} from "../../../types/Response/AdminResponse";
import { useAlert } from "../../../components/customer/AlertContext";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";

interface ISelectedAttributeRow {
  masterId: number;
  masterName: string;
  availableValues: AttributeValue[];
  selectedValues: { id: number; value: string }[];
  isLoadingValues: boolean;
  valueSearchQuery?: string;
}

interface ICustomProductAttributeRow {
  attributeName: string;
  attributeValue: string;
}

interface ICategoryNode {
  id: number;
  name: string;
  children?: ICategoryNode[];
}

const DEFAULT_ATTRIBUTE_NAMES = [
  "Flavour",
  "Age Range",
  "Texture Variation",
  "Packaging Type",
  "Breed",
  "Health Feature",
  "Unit Count",
  "Toy Type",
  "Functional Variation",
  "Toy Material Type",
  "Made In Country",
  "Free From",
  "Eco & Sustainable Features",
  "Scent Variation",
];

const AdminProductAddScreen: React.FC = () => {
  const navigate = useNavigate();
  const { showConfirm } = useAlert();

  // API Core Mutations & Lazy Queries
  const [addProduct, { isLoading: isSubmitting }] =
    useAdminAddSingleProductMutation();
  const [uploadFile, { isLoading: isUploadingFile }] = useUploadFileMutation();
  const [getAllBrands, { data: brandsList, isLoading: isLoadingBrands }] =
    useLazyGetAllBrandsQuery();
  const [
    triggerGetFullTree,
    { data: fullTreeResponse, isLoading: isLoadingFullTree },
  ] = useLazyGetCategoryQuery();

  // Attributes Engine API Hooks
  const [
    triggerGetMasters,
    { data: mastersResponse, isLoading: isLoadingMasters },
  ] = useLazyGetAttributeMaterQuery();
  const [triggerGetValues] = useLazyGetAttributeMaterValuesQuery();

  // Form Field States
  const [productName, setProductName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [sku, setSku] = useState("");
  const [barcode, setBarcode] = useState("");

  // Pricing states
  const [price, setPrice] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [discount, setDiscount] = useState("");
  const [stock, setStock] = useState("1");

  // Promotional Sales Dates
  const [saleStartDate, setSaleStartDate] = useState("");
  const [saleEndDate, setSaleEndDate] = useState("");

  // Product Variants
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");

  // Specifications, Ingredients & Marketing Fields
  const [ingredients, setIngredients] = useState("");
  const [instruction, setInstruction] = useState("");
  const [manufactureDate, setManufactureDate] = useState("");
  const [marketingText1, setMarketingText1] = useState("");
  const [marketingText2, setMarketingText2] = useState("");
  const [marketingText3, setMarketingText3] = useState("");
  const [marketingText4, setMarketingText4] = useState("");

  // Custom Product Attributes ("productAttributes" array schema)
  const [customProductAttributes, setCustomProductAttributes] = useState<
    ICustomProductAttributeRow[]
  >([
    { attributeName: "Flavour", attributeValue: "" },
    { attributeName: "Age Range", attributeValue: "" },
  ]);

  // Configuration Settings
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [subscriptionFirstDiscount, setSubscriptionFirstDiscount] =
    useState("");
  const [subscriptionRecurringDiscount, setSubscriptionRecurringDiscount] =
    useState("");
  const [freeClaimEnabled, setFreeClaimEnabled] = useState(false);
  const [freeClaimThreshold, setFreeClaimThreshold] = useState("");

  // Payload Flag Configurations
  const [isForceProduct, setIsForceProduct] = useState(false);
  const [isSponsored, setIsSponsored] = useState(false);
  const [isPetlandRecommended, setIsPetlandRecommended] = useState(false);

  // Brand Selector States
  const [brandInput, setBrandSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<IBrandResponse | null>(
    null
  );
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const brandRef = useRef<HTMLDivElement>(null);
  const masterDropdownRef = useRef<HTMLDivElement>(null);

  // Categories Selection States
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedCategoryLabels, setSelectedCategoryLabels] = useState<
    string[]
  >([]);
  const [expandedCategoryNodes, setExpandedCategoryNodes] = useState<
    Record<number, boolean>
  >({});
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  

  // Highlights & Features
  const [features, setFeatures] = useState<string[]>(["", ""]);

  // Dynamic Attribute Master Selection
  const [attributeRows, setAttributeRows] = useState<ISelectedAttributeRow[]>(
    []
  );
  const [showMasterDropdown, setShowMasterDropdown] = useState(false);
  const [masterSearchQuery, setMasterSearchQuery] = useState("");

  // Media Assets Arrays
  const [images, setImages] = useState<{ url: string; isPrimary: boolean }[]>([
    { url: "", isPrimary: true },
  ]);
  const [videoUrl, setVideoUrl] = useState("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropModalSrc, setCropModalSrc] = useState<string>("");
  const [activeCropIndex, setActiveCropIndex] = useState<number | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Initial configuration sync runs
  useEffect(() => {
    getAllBrands();
    triggerGetFullTree();
    triggerGetMasters({ pageSize: 50, pageNumber: 1, isActive: true });
  }, [getAllBrands, triggerGetFullTree, triggerGetMasters]);

  // Handle outside clicks to close active brand/attribute panels cleanly
  useEffect(() => {
    const checkOutside = (e: MouseEvent) => {
      if (brandRef.current && !brandRef.current.contains(e.target as Node)) {
        setShowBrandDropdown(false);
      }
      if (
        masterDropdownRef.current &&
        !masterDropdownRef.current.contains(e.target as Node)
      ) {
        setShowMasterDropdown(false);
      }
    };
    document.addEventListener("mousedown", checkOutside);
    return () => document.removeEventListener("mousedown", checkOutside);
  }, []);

  const mastersList = mastersResponse?.data?.data || [];
  const activeBrandOptions = (brandsList ?? []).filter(
    (b: IBrandResponse) =>
      b.name.toLowerCase().includes(brandInput.toLowerCase()) &&
      b.isActive !== false
  );
  const visibleBrandSuggestions = brandInput
    ? activeBrandOptions
    : (brandsList ?? []).slice(0, 5);

  // Pricing auto-equations calculator
  const runPriceMatrixEquations = (
    triggerSource: "price" | "sale" | "discount",
    inputVal: string
  ) => {
    const numericBasePrice =
      triggerSource === "price"
        ? parseFloat(inputVal) || 0
        : parseFloat(price) || 0;
    const numericSalePrice =
      triggerSource === "sale"
        ? parseFloat(inputVal) || 0
        : parseFloat(salePrice) || 0;
    const numericDiscountPercent =
      triggerSource === "discount"
        ? parseFloat(inputVal) || 0
        : parseFloat(discount) || 0;

    if (triggerSource === "price") {
      setPrice(inputVal);
      if (numericDiscountPercent > 0) {
        const computedSale =
          numericBasePrice - numericBasePrice * (numericDiscountPercent / 100);
        setSalePrice(computedSale > 0 ? computedSale.toFixed(2) : "");
      } else if (numericSalePrice > 0) {
        const computedDiscount =
          ((numericBasePrice - numericSalePrice) / numericBasePrice) * 100;
        setDiscount(computedDiscount > 0 ? computedDiscount.toFixed(0) : "");
      }
    }

    if (triggerSource === "sale") {
      setSalePrice(inputVal);
      if (numericBasePrice > 0 && inputVal !== "") {
        const computedDiscount =
          ((numericBasePrice - numericSalePrice) / numericBasePrice) * 100;
        setDiscount(computedDiscount > 0 ? computedDiscount.toFixed(0) : "");
      } else if (inputVal === "") {
        setDiscount("");
      }
    }

    if (triggerSource === "discount") {
      setDiscount(inputVal);
      if (numericBasePrice > 0 && inputVal !== "") {
        const computedSale =
          numericBasePrice - numericBasePrice * (numericDiscountPercent / 100);
        setSalePrice(computedSale > 0 ? computedSale.toFixed(2) : "");
      } else if (inputVal === "") {
        setSalePrice("");
      }
    }
  };

  const handleToggleCategorySelection = (catId: number, catName: string) => {
    if (selectedCategoryIds.includes(catId)) {
      setSelectedCategoryIds(selectedCategoryIds.filter((id) => id !== catId));
      setSelectedCategoryLabels(
        selectedCategoryLabels.filter((name) => name !== catName)
      );
    } else {
      setSelectedCategoryIds([...selectedCategoryIds, catId]);
      setSelectedCategoryLabels([...selectedCategoryLabels, catName]);
    }
  };

  const toggleExpandBranchNode = (nodeId: number) => {
    setExpandedCategoryNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };
useEffect(() => {
  const cachedDuplicate = localStorage.getItem("duplicated_product_payload");
  if (cachedDuplicate) {
    try {
      const prod = JSON.parse(cachedDuplicate);
      
      // 1. Basic Details
      setProductName(prod.productName ? `${prod.productName} (Copy)` : "");
      setShortDescription(prod.shortDescription || "");
      setDescription(prod.description || "");
      setSlug(prod.slug ? `${prod.slug}-copy` : "");
      setVideoUrl(prod.videoUrl || "");

      // 2. Brand Designation
      if (prod.brandId && brandsList) {
        const matchedBrand = brandsList.find((b: any) => b.id === prod.brandId);
        if (matchedBrand) {
          setSelectedBrand(matchedBrand);
        }
      }

      // 3. Categories Mapping
      if (prod.categories && Array.isArray(prod.categories)) {
        setSelectedCategoryIds(prod.categories.map((c: any) => c.categoryId || c.id));
        setSelectedCategoryLabels(prod.categories.map((c: any) => c.categoryName || c.name || "Category"));
      } else if (prod.categoryIds && Array.isArray(prod.categoryIds)) {
        setSelectedCategoryIds(prod.categoryIds);
      }

      // 4. Variant Information (Skipping SKU & Barcode)
      if (prod.variant) {
        setPrice(prod.variant.price?.toString() || "");
        setSalePrice(prod.variant.salePrice ? prod.variant.salePrice.toString() : "");
        setDiscount(prod.variant.discountPercentage ? prod.variant.discountPercentage.toString() : "");
        setStock(prod.variant.stock?.toString() || "1");
        setColor(prod.variant.color || "");
        setSize(prod.variant.size || "");
        setWeight(prod.variant.weight?.toString() || "");
        setWeightUnit(prod.variant.weightUnit || "kg");
        setLength(prod.variant.length?.toString() || "");
        setWidth(prod.variant.width?.toString() || "");
        setHeight(prod.variant.height?.toString() || "");
      }

      // 5. Images Slider Gallery
      if (prod.images && Array.isArray(prod.images)) {
        setImages(
          prod.images.map((img: any) => ({
            url: img.imageUrl || img.url,
            isPrimary: img.isPrimary || false,
          }))
        );
      }

      // 6. Master Attributes / Dropdown Specs
      if (prod.attributes && Array.isArray(prod.attributes)) {
        const mappedAttributeRows = prod.attributes.map((attr: any) => ({
          masterId: attr.attributeId,
          masterName: attr.attributeName || "Attribute",
          availableValues: [],
          selectedValues: (attr.attributeValueIds || []).map((valId: number) => ({
            id: valId,
            value: `Value #${valId}`,
          })),
          isLoadingValues: false,
        }));
        setAttributeRows(mappedAttributeRows);
      }

      // 7. Custom Key-Value Product Attributes
      if (prod.productAttributes && Array.isArray(prod.productAttributes)) {
        setCustomProductAttributes(
          prod.productAttributes.map((pa: any) => ({
            attributeName: pa.attributeName || "",
            attributeValue: pa.attributeValue || pa.customValue || "",
          }))
        );
      }

      // 8. Features Highlights
      if (prod.features && Array.isArray(prod.features)) {
        setFeatures(prod.features.map((f: any) => f.feature || f));
      }

      // 9. Operational Flags & Subscriptions
      if (prod.subscription) {
        setSubscriptionEnabled(prod.subscription.isEnabled || false);
        setSubscriptionFirstDiscount(prod.subscription.firstOrderDiscount?.toString() || "");
        setSubscriptionRecurringDiscount(prod.subscription.recurringDiscount?.toString() || "");
      }

      if (prod.freeClaim) {
        setFreeClaimEnabled(prod.freeClaim.isEnabled || false);
        setFreeClaimThreshold(prod.freeClaim.cartAmountThreshold?.toString() || "");
      }

      setIsForceProduct(prod.isForceProduct || false);
      setIsSponsored(prod.isSponsored || false);
      setIsPetlandRecommended(prod.isPetlandRecommended || false);

      // Clear cached data so it doesn't persist on subsequent normal "Add" clicks
      localStorage.removeItem("duplicated_product_payload");
    } catch (e) {
      console.error("Error parsing duplicated product payload", e);
    }
  }
}, [brandsList]);

  const checkCategoryMatchesQuery = (
    node: ICategoryNode,
    query: string
  ): boolean => {
    if (!query) return true;
    if (node.name.toLowerCase().includes(query.toLowerCase())) return true;
    if (node.children && node.children.length > 0) {
      return node.children.some((child) =>
        checkCategoryMatchesQuery(child, query)
      );
    }
    return false;
  };

  const renderHierarchicalTreeRows = (nodes: ICategoryNode[] = []) => {
    const visibleNodes = nodes.filter((node) =>
      checkCategoryMatchesQuery(node, categorySearchQuery)
    );

    return (
      <div className="space-y-2 pl-2 border-l border-slate-100 ml-1 mt-1">
        {visibleNodes.map((node) => {
          const hasChildren = node.children && node.children.length > 0;
          const isExpanded = categorySearchQuery
            ? true
            : !!expandedCategoryNodes[node.id];
          const isChecked = selectedCategoryIds.includes(node.id);

          return (
            <div key={node.id} className="text-xs">
              <div className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-slate-50 select-none">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => toggleExpandBranchNode(node.id)}
                    className="text-slate-400 p-0.5"
                  >
                    {isExpanded ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </button>
                ) : (
                  <div className="w-[18px]" />
                )}
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() =>
                    handleToggleCategorySelection(node.id, node.name)
                  }
                  className="accent-[#2F5E2E] h-3.5 w-3.5 border-slate-300 rounded-sm cursor-pointer"
                />
                <span
                  className="font-medium text-slate-700 cursor-pointer"
                  onClick={() =>
                    handleToggleCategorySelection(node.id, node.name)
                  }
                >
                  {node.name}
                </span>
              </div>
              {hasChildren &&
                isExpanded &&
                renderHierarchicalTreeRows(node.children)}
            </div>
          );
        })}
      </div>
    );
  };

  // --- MULTI-SELECT MASTER ATTRIBUTES ENGINE ---
  const handleToggleMasterAttribute = async (
    targetMasterId: number,
    targetMasterName: string
  ) => {
    const isAlreadySelected = attributeRows.some(
      (row) => row.masterId === targetMasterId
    );

    if (isAlreadySelected) {
      setAttributeRows((prev) =>
        prev.filter((row) => row.masterId !== targetMasterId)
      );
    } else {
      const newRow: ISelectedAttributeRow = {
        masterId: targetMasterId,
        masterName: targetMasterName,
        availableValues: [],
        selectedValues: [],
        isLoadingValues: true,
        valueSearchQuery: "",
      };
      setAttributeRows((prev) => [...prev, newRow]);

      try {
        const result = await triggerGetValues(targetMasterId).unwrap();
        setAttributeRows((prev) =>
          prev.map((row) =>
            row.masterId === targetMasterId
              ? {
                  ...row,
                  availableValues: result?.data || [],
                  isLoadingValues: false,
                }
              : row
          )
        );
      } catch (error: any) {
        showConfirm({
          title: "Error Fetching Data",
          message:
            error?.data?.message ||
            "Could not fetch attribute configuration options from the server.",
          type: "error",
          confirmText: "Continue",
        });
        setAttributeRows((prev) =>
          prev.filter((row) => row.masterId !== targetMasterId)
        );
      }
    }
  };

  const handleToggleValueSelection = (
    masterId: number,
    valId: number,
    valString: string
  ) => {
    setAttributeRows((prev) =>
      prev.map((row) => {
        if (row.masterId !== masterId) return row;
        const isAlreadyPicked = row.selectedValues.some(
          (item) => item.id === valId
        );
        const nextSelected = isAlreadyPicked
          ? row.selectedValues.filter((item) => item.id !== valId)
          : [...row.selectedValues, { id: valId, value: valString }];
        return { ...row, selectedValues: nextSelected };
      })
    );
  };

  // Handlers for Custom productAttributes Array
  const addCustomAttributeRow = () => {
    setCustomProductAttributes([
      ...customProductAttributes,
      { attributeName: "", attributeValue: "" },
    ]);
  };

  const removeCustomAttributeRow = (index: number) => {
    setCustomProductAttributes(
      customProductAttributes.filter((_, idx) => idx !== index)
    );
  };

  const updateCustomAttributeRow = (
    index: number,
    key: "attributeName" | "attributeValue",
    val: string
  ) => {
    const updated = [...customProductAttributes];
    updated[index][key] = val;
    setCustomProductAttributes(updated);
  };

  // Image Upload and Crop Handlers
  const handleImageFileChange = (file: File, index: number) => {
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setCropModalSrc(reader.result?.toString() || "");
      setActiveCropIndex(index);
      setCropModalOpen(true);
    });
    reader.readAsDataURL(file);
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;
    const initialCrop: Crop = {
      unit: "%",
      x: 10,
      y: 10,
      width: 80,
      height: 80,
    };
    setCrop(initialCrop);
  };

  const handleApplyCrop = async () => {
    if (!imgRef.current || !completedCrop || activeCropIndex === null) return;

    try {
      const croppedFile = await getCroppedImgFile(
        imgRef.current,
        completedCrop,
        `cropped_image_${Date.now()}.jpg`
      );

      const response = await uploadFile({
        File: croppedFile,
        FolderName: "Products/Images",
      }).unwrap();

      const updated = [...images];
      updated[activeCropIndex].url = response.fileUrl;
      setImages(updated);

      setCropModalOpen(false);
      setCropModalSrc("");
      setActiveCropIndex(null);
    } catch (err: any) {
      showConfirm({
        title: "Upload Failed",
        message:
          err?.data?.message || "Cropped image upload encountered an issue.",
        type: "error",
        confirmText: "Close",
      });
    }
  };

  const getCroppedImgFile = (
    image: HTMLImageElement,
    cropConfig: PixelCrop,
    fileName: string
  ): Promise<File> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = cropConfig.width;
    canvas.height = cropConfig.height;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.drawImage(
        image,
        cropConfig.x * scaleX,
        cropConfig.y * scaleY,
        cropConfig.width * scaleX,
        cropConfig.height * scaleY,
        0,
        0,
        cropConfig.width,
        cropConfig.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        const file = new File([blob], fileName, { type: "image/jpeg" });
        resolve(file);
      }, "image/jpeg");
    });
  };

  const handleVideoFileChange = async (file: File) => {
    try {
      const response = await uploadFile({
        File: file,
        FolderName: "Products/Videos",
      }).unwrap();
      setVideoUrl(response.fileUrl);
    } catch (err: any) {
      showConfirm({
        title: "Upload Failed",
        message:
          err?.data?.message || "Video file asset upload encountered an issue.",
        type: "error",
        confirmText: "Close",
      });
    }
  };

  const addNewImageSlot = () =>
    setImages([...images, { url: "", isPrimary: false }]);

  const removeImageSlot = (index: number) => {
    if (images.length === 1) return;
    const updated = images.filter((_, i) => i !== index);
    if (!updated.some((img) => img.isPrimary)) updated[0].isPrimary = true;
    setImages(updated);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !productName ||
      !sku ||
      !selectedBrand ||
      selectedCategoryIds.length === 0
    ) {
      showConfirm({
        title: "Missing Required Parameters",
        message:
          "Please fill out all basic required parameters (SKU, Product Name, Brand, and Categories).",
        type: "error",
        confirmText: "Review Form",
      });
      return;
    }

    // Dynamic Master Attributes Mapping
    const attributesPayload = attributeRows
      .filter((row) => row.masterId > 0 && row.selectedValues.length > 0)
      .map((row) => ({
        attributeId: row.masterId,
        attributeName: row.masterName,
        attributeValueIds: row.selectedValues.map((v) => v.id),
        isFilterable: true,
        isVisibleOnProductPage: true,
      }));

    // Custom Key-Value Product Attributes Mapping
    const productAttributesPayload = customProductAttributes
      .filter((row) => row.attributeName.trim() && row.attributeValue.trim())
      .map((row) => ({
        attributeDefinitionId: 0,
        attributeName: row.attributeName.trim(),
        attributeValueId: 0,
        attributeValue: row.attributeValue.trim(),
        customValue: row.attributeValue.trim(),
        isFilterable: true,
        isVisibleOnProductPage: true,
      }));

    // Specifications Array Mapping
    const specificationsPayload: Array<{
      specificationId: number;
      section: string;
      specificationKey: string;
      specificationValue: string;
    }> = [];

    if (ingredients.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Ingredients",
        specificationKey: "Product Ingredients",
        specificationValue: ingredients.trim(),
      });
    }
    if (instruction.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Instructions",
        specificationKey: "Usage Instructions",
        specificationValue: instruction.trim(),
      });
    }
    if (manufactureDate.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Manufacture",
        specificationKey: "Manufacture Date",
        specificationValue: manufactureDate.trim(),
      });
    }
    if (marketingText1.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Marketing",
        specificationKey: "Marketing Text 1",
        specificationValue: marketingText1.trim(),
      });
    }
    if (marketingText2.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Marketing",
        specificationKey: "Marketing Text 2",
        specificationValue: marketingText2.trim(),
      });
    }
    if (marketingText3.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Marketing",
        specificationKey: "Marketing Text 3",
        specificationValue: marketingText3.trim(),
      });
    }
    if (marketingText4.trim()) {
      specificationsPayload.push({
        specificationId: 0,
        section: "Marketing",
        specificationKey: "Marketing Text 4",
        specificationValue: marketingText4.trim(),
      });
    }

    const payload: IAddEditProductSingleRequest & {
      isPetlandRecommended: boolean;
      isSponsored: boolean;
      isForceProduct: boolean;
      isFeatured: boolean;
      productAttributes: typeof productAttributesPayload;
    } = {
      productId: 0,
      productName,
      productCode: "",
      slug:
        slug ||
        productName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, ""),
      brandId: selectedBrand.id,
      categoryIds: selectedCategoryIds,
      shortDescription: shortDescription || "",
      description: description || "",
      videoUrl: videoUrl || "",
      isActive: true,
      isPublished: true,
      isFeatured: true,
      isPetlandRecommended: isPetlandRecommended,
      isSponsored: isSponsored,
      isForceProduct: isForceProduct,
      variant: {
        variantId: 0,
        sku,
        barcode: barcode || "",
        price: Number(price) || 0,
        salePrice: Number(salePrice) || 0,
        discountPercentage: Number(discount) || 0,
        saleStartDate: saleStartDate
          ? new Date(saleStartDate).toISOString()
          : new Date().toISOString(),
        saleEndDate: saleEndDate
          ? new Date(saleEndDate).toISOString()
          : new Date().toISOString(),
        stock: Number(stock) || 0,
        color: color || "",
        size: size || "",
        weight: weight ? Number(weight) || 0 : 0,
        weightUnit: weightUnit,
        length: length ? Number(length) || 0 : 0,
        width: width ? Number(width) || 0 : 0,
        height: height ? Number(height) || 0 : 0,
      },
      images: images
        .filter((img) => img.url)
        .map((img, index) => ({
          imageId: 0,
          imageUrl: img.url,
          isPrimary: img.isPrimary,
          sortOrder: index + 1,
        })),
      features: features
        .filter((f) => f.trim())
        .map((f, index) => ({
          featureId: 0,
          feature: f,
          sortOrder: index + 1,
        })),
      specifications: specificationsPayload,
      attributes: attributesPayload,
      productAttributes: productAttributesPayload,
      seo: {
        seoTitle: productName,
        seoDescription: shortDescription || "",
        searchKeywords: "",
      },
      subscription: {
        isEnabled: subscriptionEnabled,
        firstOrderDiscount: subscriptionEnabled
          ? Number(subscriptionFirstDiscount) || 0
          : 0,
        recurringDiscount: subscriptionEnabled
          ? Number(subscriptionRecurringDiscount) || 0
          : 0,
      },
      freeClaim: {
        isEnabled: freeClaimEnabled,
        cartAmountThreshold: freeClaimEnabled
          ? Number(freeClaimThreshold) || 0
          : 0,
      },
      productFamily: { hasFamily: false, rootProductId: 0, taggedProducts: [] },
    };

    try {
      await addProduct(payload).unwrap();
      showConfirm({
        title: "Product Created",
        message: "The product has been successfully added.",
        type: "success",
        confirmText: "Continue",
        onConfirm: () => {
          navigate("/admin/product/productmanagement");
        },
      });
    } catch (error: any) {
      showConfirm({
        title: "Creation Failed",
        message:
          error?.data?.message ||
          "Could not complete the request to add this product configuration downstream.",
        type: "error",
        confirmText: "Close",
      });
    }
  };

  const filteredMasters = mastersList.filter((m: AttributeMaster) =>
    m.name.toLowerCase().includes(masterSearchQuery.toLowerCase())
  );

  return (
    <form
      onSubmit={handleFormSubmit}
      className="px-4 pb-16 text-left space-y-6 max-w-5xl mx-auto selection:bg-[#A8D97B]/30 text-slate-700 text-xs"
    >
      {/* SCREEN HEADER CONTROLS */}
      <div className="flex items-center gap-4 py-4 border-b border-slate-100">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 bg-white rounded-xl border border-slate-200 hover:bg-slate-50 transition"
        >
          <ArrowLeft size={16} className="text-slate-600" />
        </button>
        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight uppercase">
            Add Product
          </h2>
        </div>
      </div>

      {/* PRODUCT CORE DIRECTORIES DETAILS */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <p className="text-slate-900 text-sm font-black uppercase tracking-wide border-b pb-1.5 border-slate-100">
          Product Details
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInputComp
            value={productName}
            onChangeText={setProductName}
            label="Product Name *"
            showLabel
            placeholder="Enter product display name"
          />
          <TextInputComp
            value={sku}
            onChangeText={setSku}
            label="SKU *"
            showLabel
            placeholder="Enter unique item inventory card index code"
          />
          {/* <TextInputComp
            value={productCode}
            onChangeText={setProductCode}
            label="Product Code"
            showLabel
            placeholder="Enter internal system code parameters"
          /> */}
          <TextInputComp
            value={slug}
            onChangeText={setSlug}
            label="Slug"
            showLabel
            placeholder="Auto-generated if left empty"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <div className="space-y-1 relative" ref={brandRef}>
            <label className="text-xs font-bold text-slate-600">Brand</label>
            <div className="relative">
              <input
                type="text"
                placeholder={
                  isLoadingBrands
                    ? "Loading brand options..."
                    : "Search brand..."
                }
                value={selectedBrand ? selectedBrand.name : brandInput}
                onChange={(e) => {
                  if (selectedBrand) setSelectedBrand(null);
                  setBrandSearchQuery(e.target.value);
                  setShowBrandDropdown(true);
                }}
                onFocus={() => setShowBrandDropdown(true)}
                disabled={isLoadingBrands}
                className="w-full h-9 text-xs px-3 pl-8 border border-slate-200 bg-white rounded-xl outline-hidden focus:border-[#2F5E2E] transition font-medium"
              />
              <Search
                size={13}
                className="absolute left-3 top-3 text-slate-400"
              />
              {selectedBrand && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedBrand(null);
                    setBrandSearchQuery("");
                  }}
                  className="absolute right-3 top-2.5 text-[10px] font-black text-rose-500 bg-rose-50 border border-rose-100 px-1.5 py-0.5 rounded-md uppercase tracking-wider"
                >
                  Clear
                </button>
              )}
            </div>

            {showBrandDropdown && (
              <div className="absolute top-full left-0 right-0 max-h-44 overflow-y-auto bg-white border border-slate-200 shadow-xl rounded-xl z-50 mt-1 divide-y divide-slate-50">
                {visibleBrandSuggestions.length === 0 ? (
                  <p className="p-3 text-xs italic text-slate-400">
                    No matching brands cataloged.
                  </p>
                ) : (
                  visibleBrandSuggestions.map((brand: IBrandResponse) => (
                    <button
                      key={brand.id}
                      type="button"
                      onClick={() => {
                        setSelectedBrand(brand);
                        setShowBrandDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 font-medium flex items-center gap-2 transition"
                    >
                      {brand.logoUrl && (
                        <img
                          src={brand.logoUrl}
                          alt=""
                          className="w-4 h-4 rounded object-cover border"
                        />
                      )}
                      <span>{brand.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <TextInputComp
            value={barcode}
            onChangeText={setBarcode}
            label="Barcode"
            showLabel
            placeholder="enter barcode"
          />
        </div>

        <div className="space-y-2 pt-1">
          <label className="text-xs font-bold text-slate-600 block">
            Assigned Categories *
          </label>
          <div className="border border-slate-200 p-3 rounded-xl bg-slate-50/40 flex flex-wrap gap-2 min-h-[40px]">
            {selectedCategoryLabels.length === 0 && (
              <span className="text-xs text-slate-400 italic">
                No categories linked. Use the tree below to pick nodes.
              </span>
            )}
            {selectedCategoryLabels.map((label, idx) => (
              <span
                key={idx}
                className="bg-emerald-50 text-emerald-800 px-2.5 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 border border-emerald-100 uppercase tracking-wide"
              >
                {label}
                <button
                  type="button"
                  onClick={() =>
                    handleToggleCategorySelection(
                      selectedCategoryIds[idx],
                      label
                    )
                  }
                  className="text-rose-500 hover:text-rose-700 font-bold text-xs"
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <div className="relative w-full max-w-xs pt-1">
            <Search
              size={11}
              className="absolute left-3 top-3 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search categories tree..."
              value={categorySearchQuery}
              onChange={(e) => setCategorySearchQuery(e.target.value)}
              className="w-full h-8 text-[11px] px-2 pl-7 border border-slate-200 rounded-lg outline-none focus:border-[#2F5E2E] bg-slate-50/50"
            />
          </div>

          <div className="border border-slate-200 rounded-xl p-3 max-h-52 overflow-y-auto bg-white shadow-inner">
            {isLoadingFullTree ? (
              <div className="flex items-center gap-1.5 text-slate-400 p-2 italic">
                <Loader2 size={13} className="animate-spin" /> Pulling
                structural layout index map branches...
              </div>
            ) : fullTreeResponse ? (
              renderHierarchicalTreeRows(fullTreeResponse)
            ) : (
              <p className="text-slate-400 italic pl-1">
                No operational storage categories synced downstream.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <TextInputComp
            value={shortDescription}
            onChangeText={setShortDescription}
            label="Short Description"
            placeholder="Enter Short Description"
          />
          <TextAreaComp
            value={description}
            onChangeText={setDescription}
            label="Description"
            placeholder="Enter Description"
          />
        </div>
      </div>

      {/* MULTI-SELECT SPECIFICATIONS & MASTER PRODUCT ATTRIBUTES */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <p className="text-slate-900 text-sm font-black uppercase tracking-wide border-b pb-1.5 border-slate-100">
          Master Attributes Selection
        </p>

        {/* MASTER ATTRIBUTE DROPDOWN SYSTEM */}
        <div className="space-y-1 relative" ref={masterDropdownRef}>
          <label className="text-xs font-bold text-slate-600 block">
            Select Master Attributes
          </label>
          <div
            onClick={() => setShowMasterDropdown(!showMasterDropdown)}
            className="w-full min-h-9 px-3 py-1.5 border border-slate-200 bg-white rounded-xl text-xs font-medium focus:border-[#2F5E2E] flex items-center justify-between cursor-pointer select-none wrap"
          >
            <div className="flex flex-wrap gap-1.5">
              {attributeRows.length === 0 ? (
                <span className="text-slate-400">
                  -- Click to select multiple attributes --
                </span>
              ) : (
                attributeRows.map((row) => (
                  <span
                    key={row.masterId}
                    className="bg-slate-100 text-slate-800 px-2 py-0.5 rounded-md text-[10px] font-bold flex items-center gap-1 border border-slate-200"
                  >
                    {row.masterName}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleMasterAttribute(
                          row.masterId,
                          row.masterName
                        );
                      }}
                      className="text-rose-500 font-bold hover:text-rose-700"
                    >
                      ×
                    </button>
                  </span>
                ))
              )}
            </div>
            <ChevronDown size={14} className="text-slate-400 shrink-0 ml-2" />
          </div>

          {showMasterDropdown && (
            <div className="absolute top-full left-0 right-0 max-h-56 overflow-y-auto bg-white border border-slate-200 shadow-xl rounded-xl z-50 mt-1 p-2 space-y-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search attribute types..."
                  value={masterSearchQuery}
                  onChange={(e) => setMasterSearchQuery(e.target.value)}
                  className="w-full h-8 text-[11px] px-2 pl-7 border border-slate-200 rounded-lg outline-none focus:border-[#2F5E2E]"
                />
                <Search
                  size={11}
                  className="absolute left-2 top-2.5 text-slate-400"
                />
              </div>

              <div className="divide-y divide-slate-50 max-h-36 overflow-y-auto">
                {filteredMasters.length === 0 ? (
                  <p className="p-2 text-xs italic text-slate-400">
                    No matches found.
                  </p>
                ) : (
                  filteredMasters.map((m: AttributeMaster) => {
                    const isChecked = attributeRows.some(
                      (row) => row.masterId === m.id
                    );
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() =>
                          handleToggleMasterAttribute(m.id, m.name)
                        }
                        className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 font-medium flex items-center justify-between transition"
                      >
                        <span>{m.name}</span>
                        {isChecked && (
                          <Check size={12} className="text-[#2F5E2E]" />
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* CONSOLIDATED VALUES CONTAINER */}
        {attributeRows.length > 0 && (
          <div className="border border-slate-100 bg-slate-50/30 p-4 rounded-xl space-y-4 mt-2">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
              Link Attribute Target Value Sets
            </p>
            <div className="grid grid-cols-1 gap-4">
              {attributeRows.map((row) => {
                const filteredValues = row.availableValues.filter((v) =>
                  v.value
                    .toLowerCase()
                    .includes((row.valueSearchQuery || "").toLowerCase())
                );

                return (
                  <div
                    key={row.masterId}
                    className="bg-white p-3 rounded-xl border border-slate-200/60 space-y-2"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b pb-1.5 border-slate-100">
                      <span className="font-bold text-slate-800 text-xs uppercase tracking-wide">
                        {row.masterName}
                      </span>

                      {!row.isLoadingValues &&
                        row.availableValues.length > 0 && (
                          <div className="relative w-full max-w-[200px]">
                            <input
                              type="text"
                              placeholder="Filter values..."
                              value={row.valueSearchQuery || ""}
                              onChange={(e) =>
                                setAttributeRows((prev) =>
                                  prev.map((r) =>
                                    r.masterId === row.masterId
                                      ? {
                                          ...r,
                                          valueSearchQuery: e.target.value,
                                        }
                                      : r
                                  )
                                )
                              }
                              className="w-full h-7 text-[11px] px-2 pl-6 border border-slate-200 rounded-lg outline-none focus:border-[#2F5E2E] bg-slate-50/50"
                            />
                            <Search
                              size={10}
                              className="absolute left-2 top-2 text-slate-400"
                            />
                          </div>
                        )}
                    </div>

                    {row.isLoadingValues ? (
                      <div className="h-9 flex items-center gap-1.5 text-slate-400 italic">
                        <Loader2 size={12} className="animate-spin" /> Pulling
                        values options...
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {row.availableValues.length === 0 ? (
                          <span className="text-slate-400 italic p-1">
                            No criteria values parsed under this selector.
                          </span>
                        ) : filteredValues.length === 0 ? (
                          <span className="text-slate-400 italic p-1">
                            No values match filter parameters.
                          </span>
                        ) : (
                          filteredValues.map((v: AttributeValue) => {
                            const isChecked = row.selectedValues.some(
                              (item) => item.id === v.id
                            );
                            return (
                              <button
                                key={v.id}
                                type="button"
                                onClick={() =>
                                  handleToggleValueSelection(
                                    row.masterId,
                                    v.id,
                                    v.value
                                  )
                                }
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] border font-bold uppercase tracking-wide cursor-pointer transition select-none ${
                                  isChecked
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200 shadow-3xs font-black scale-98"
                                    : "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
                                }`}
                              >
                                {v.value}
                                {isChecked && (
                                  <X
                                    size={10}
                                    className="text-emerald-600 ml-0.5"
                                  />
                                )}
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* CUSTOM PRODUCT ATTRIBUTES (productAttributes Schema) */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <div className="flex justify-between items-center border-b pb-1.5 border-slate-100">
          <p className="text-slate-900 text-sm font-black uppercase tracking-wide">
            Product Custom Attributes (Key-Value)
          </p>
          <button
            type="button"
            onClick={addCustomAttributeRow}
            className="inline-flex items-center gap-1 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-xl shadow-xs transition"
          >
            <Plus size={12} /> Add Attribute Field
          </button>
        </div>

        <div className="space-y-3">
          {customProductAttributes.map((attr, idx) => (
            <div
              key={idx}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center bg-slate-50/50 p-2.5 rounded-xl border border-slate-100"
            >
              <div className="md:col-span-5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Attribute Name
                </label>
                <input
                  type="text"
                  list="attribute-names-list"
                  placeholder="e.g. Flavour, Breed, Packaging Type"
                  value={attr.attributeName}
                  onChange={(e) =>
                    updateCustomAttributeRow(
                      idx,
                      "attributeName",
                      e.target.value
                    )
                  }
                  className="w-full h-8 text-xs px-2.5 border border-slate-200 bg-white rounded-lg focus:border-[#2F5E2E] outline-none font-medium"
                />
              </div>

              <div className="md:col-span-6">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Attribute Value
                </label>
                <input
                  type="text"
                  placeholder="Enter attribute value string..."
                  value={attr.attributeValue}
                  onChange={(e) =>
                    updateCustomAttributeRow(
                      idx,
                      "attributeValue",
                      e.target.value
                    )
                  }
                  className="w-full h-8 text-xs px-2.5 border border-slate-200 bg-white rounded-lg focus:border-[#2F5E2E] outline-none font-medium"
                />
              </div>

              <div className="md:col-span-1 flex justify-end pt-4 md:pt-0">
                <button
                  type="button"
                  onClick={() => removeCustomAttributeRow(idx)}
                  className="text-rose-500 hover:text-rose-700 p-1.5 rounded-lg hover:bg-rose-50 transition"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}

          {/* Datalist for preset quick selections */}
          <datalist id="attribute-names-list">
            {DEFAULT_ATTRIBUTE_NAMES.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>
      </div>

      {/* DYNAMIC AUTO-CONNECTED FINANCE GRID */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <p className="text-slate-900 text-sm font-black uppercase tracking-wide border-b pb-1.5 border-slate-100">
          Pricing Structures & Stock Count
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TextInputComp
            value={price}
            onChangeText={(val) => runPriceMatrixEquations("price", val)}
            label="Base Item AED Price *"
            showLabel
            placeholder="0.00"
          />
          <TextInputComp
            value={salePrice}
            onChangeText={(val) => runPriceMatrixEquations("sale", val)}
            label="Promotional Sale Cost"
            showLabel
            placeholder="0.00"
          />
          <TextInputComp
            value={discount}
            onChangeText={(val) => runPriceMatrixEquations("discount", val)}
            label="Discount Offer Percentage"
            showLabel
            placeholder="%"
          />
          <TextInputComp
            value={stock}
            onChangeText={setStock}
            type="number"
            label="Inventory Stock Quantity *"
            showLabel
          />
        </div>

        {/* PROMOTIONAL RANGE CALENDAR TIMERS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 flex items-center gap-1">
              <Calendar size={13} className="text-[#2F5E2E]" /> Discount Offer
              Active Start Time
            </label>
            <input
              type="datetime-local"
              value={saleStartDate}
              onChange={(e) => setSaleStartDate(e.target.value)}
              className="w-full h-9 text-xs px-3 border border-slate-200 bg-white rounded-xl focus:outline-none focus:border-[#2F5E2E] text-slate-700 font-medium"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 flex items-center gap-1">
              <Calendar size={13} className="text-[#2F5E2E]" /> Discount Offer
              Active End Time
            </label>
            <input
              type="datetime-local"
              value={saleEndDate}
              onChange={(e) => setSaleEndDate(e.target.value)}
              className="w-full h-9 text-xs px-3 border border-slate-200 bg-white rounded-xl focus:outline-none focus:border-[#2F5E2E] text-slate-700 font-medium"
            />
          </div>
        </div>
      </div>

      {/* VALUE FLAGS CHECKBOX INTERFACES CONFIG */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-3xs select-none">
        {/* FORCE RECOMMENDATION CHECKBOX */}
        <label
          className={`flex items-center gap-2 border p-2.5 rounded-xl transition ${
            isSponsored || isPetlandRecommended || freeClaimEnabled
              ? "bg-slate-100 opacity-50 cursor-not-allowed"
              : "bg-slate-50/40 hover:bg-slate-50 cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={isForceProduct}
            disabled={isSponsored || isPetlandRecommended || freeClaimEnabled}
            onChange={(e) => {
              const checked = e.target.checked;
              setIsForceProduct(checked);
              if (checked) {
                setIsSponsored(false);
                setIsPetlandRecommended(false);
                setFreeClaimEnabled(false);
                setFreeClaimThreshold("");
              }
            }}
            className="accent-[#2F5E2E] h-4 w-4 rounded-sm disabled:cursor-not-allowed"
          />
          <span className="font-bold text-slate-700">Force Recommendation</span>
        </label>

        {/* SPONSORED AD CAMPAIGN CHECKBOX */}
        <label
          className={`flex items-center gap-2 border p-2.5 rounded-xl transition ${
            isForceProduct || freeClaimEnabled
              ? "bg-slate-100 opacity-50 cursor-not-allowed"
              : "bg-slate-50/40 hover:bg-slate-50 cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={isSponsored}
            disabled={isForceProduct || freeClaimEnabled}
            onChange={(e) => setIsSponsored(e.target.checked)}
            className="accent-[#2F5E2E] h-4 w-4 rounded-sm disabled:cursor-not-allowed"
          />
          <span className="font-bold text-slate-700">
            Sponsored Ad Campaign
          </span>
        </label>

        {/* PETLAND RECOMMENDED CHECKBOX */}
        <label
          className={`flex items-center gap-2 border p-2.5 rounded-xl transition ${
            isForceProduct || freeClaimEnabled
              ? "bg-slate-100 opacity-50 cursor-not-allowed"
              : "bg-slate-50/40 hover:bg-slate-50 cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={isPetlandRecommended}
            disabled={isForceProduct || freeClaimEnabled}
            onChange={(e) => setIsPetlandRecommended(e.target.checked)}
            className="accent-[#2F5E2E] h-4 w-4 rounded-sm disabled:cursor-not-allowed"
          />
          <span className="font-bold text-slate-700">Petland Recommended</span>
        </label>
      </div>

      {/* GIFT CLAIM ELIGIBILITY SECTION CARD */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-3xs select-none">
        <label
          className={`flex items-center gap-2 border p-2.5 rounded-xl transition ${
            isForceProduct || isSponsored || isPetlandRecommended
              ? "bg-slate-100 opacity-50 cursor-not-allowed"
              : "bg-slate-50/40 hover:bg-slate-50 cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={freeClaimEnabled}
            disabled={isForceProduct || isSponsored || isPetlandRecommended}
            onChange={(e) => {
              const checked = e.target.checked;
              setFreeClaimEnabled(checked);
              if (checked) {
                setIsForceProduct(false);
                setIsSponsored(false);
                setIsPetlandRecommended(false);
              } else {
                setFreeClaimThreshold("");
              }
            }}
            className="accent-[#2F5E2E] h-4 w-4 rounded-sm disabled:cursor-not-allowed"
          />
          <span className="font-bold text-slate-700">
            Gift Claim Eligibility
          </span>
        </label>
        {freeClaimEnabled &&
          !isForceProduct &&
          !isSponsored &&
          !isPetlandRecommended && (
            <div className="animate-in fade-in duration-200">
              <TextInputComp
                value={freeClaimThreshold}
                onChangeText={setFreeClaimThreshold}
                label="Minimum Cart Threshold Required (AED)"
                showLabel
                placeholder="e.g. 300"
              />
            </div>
          )}
      </div>

      {/* EXTENDED OPERATIONAL PARAMETERS MATRIX FIELD */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <div className="flex items-center justify-between border-b pb-1.5 border-slate-100">
          <p className="text-slate-900 text-sm font-black uppercase tracking-wide">
            Autoship Subscription
          </p>
          <div className="flex items-center gap-2 select-none">
            <span className="font-bold text-xs text-slate-500 uppercase">
              Enable Subscriptions
            </span>
            <button
              type="button"
              onClick={() => setSubscriptionEnabled(!subscriptionEnabled)}
              className={`w-9 h-5 rounded-full p-0.5 flex items-center transition-colors border shadow-inner ${
                subscriptionEnabled
                  ? "bg-[#2F5E2E] border-[#2F5E2E]"
                  : "bg-slate-200 border-slate-300"
              }`}
            >
              <div
                className={`w-3.5 h-3.5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
                  subscriptionEnabled ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        </div>

        {subscriptionEnabled ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-1 duration-200">
            <TextInputComp
              value={subscriptionFirstDiscount}
              onChangeText={setSubscriptionFirstDiscount}
              label="First Recurring Sub Discount Percentage (%)"
              showLabel
              placeholder="e.g. 15"
            />
            <TextInputComp
              value={subscriptionRecurringDiscount}
              onChangeText={setSubscriptionRecurringDiscount}
              label="Ongoing Recurring Sub Discount Percentage (%)"
              showLabel
              placeholder="e.g. 5"
            />
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">
            Subscription for product are turned off. Toggle the switch above to
            turn on.
          </p>
        )}
      </div>

      {/* STRUCTURAL PROPERTIES VARIANT FIELDS */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <p className="text-slate-900 text-sm font-black uppercase tracking-wide border-b pb-1.5 border-slate-100">
          Physical Dimensions & Variants
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TextInputComp
            value={color}
            onChangeText={setColor}
            label="Item Color Variant"
            showLabel
            placeholder="e.g. Blue"
          />
          <TextInputComp
            value={size}
            onChangeText={setSize}
            label="Item Size Variant"
            showLabel
            placeholder="e.g. Adult, Puppy, XL"
          />
          <TextInputComp
            value={weight}
            onChangeText={setWeight}
            label="Gross Mass Weight"
            showLabel
            placeholder="e.g. 10"
          />
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 block">
              Weight Unit
            </label>
            <select
              value={weightUnit}
              onChange={(e) => setWeightUnit(e.target.value)}
              className="w-full h-9 px-2 border border-slate-200 bg-white rounded-xl text-xs font-medium focus:border-[#2F5E2E] cursor-pointer outline-hidden"
            >
              <option value="kg">Kilograms (kg)</option>
              <option value="g">Grams (g)</option>
              <option value="lbs">Pounds (lbs)</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <TextInputComp
            value={length}
            onChangeText={setLength}
            type="number"
            label="Item Length (cm)"
            showLabel
            placeholder="0.00"
          />
          <TextInputComp
            value={width}
            onChangeText={setWidth}
            type="number"
            label="Item Width (cm)"
            showLabel
            placeholder="0.00"
          />
          <TextInputComp
            value={height}
            onChangeText={setHeight}
            type="number"
            label="Item Height (cm)"
            showLabel
            placeholder="0.00"
          />
        </div>
      </div>

      {/* VISUAL IMAGE MEDIA ASSETS SYSTEM GALLERY LAYOUT */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-4">
        <div className="flex justify-between items-center border-b pb-2 border-slate-100">
          <p className="text-slate-900 text-sm font-black uppercase tracking-wide">
            Product Images
          </p>
          <button
            type="button"
            onClick={addNewImageSlot}
            className="inline-flex items-center gap-1 bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-xl shadow-xs transition"
          >
            <Plus size={13} /> Add Secondary Image Slot
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((img, i) => (
            <div
              key={i}
              className="border border-slate-200 p-3 rounded-xl bg-slate-50/30 space-y-2 relative"
            >
              <div className="flex justify-between items-center border-b pb-1 border-slate-100">
                <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                  Image #{i + 1}{" "}
                  {img.url && img.url === images[0].url && (
                    <span className="text-[#2F5E2E] font-black">
                      (Main Thumbnail)
                    </span>
                  )}
                </span>
                {images.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeImageSlot(i)}
                    className="text-rose-500 hover:text-rose-700"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
              <MediaUploadComp
                label={
                  i === 0
                    ? "Main Display Image"
                    : `Secondary Image Slider Element`
                }
                value={img.url}
                onChange={(val) => {
                  const updated = [...images];
                  updated[i].url = val;
                  setImages(updated);
                }}
                onFileSelect={(file) => handleImageFileChange(file, i)}
              />
              {i !== 0 && img.url && (
                <button
                  type="button"
                  onClick={() => {
                    const updated = [...images];
                    const [target] = updated.splice(i, 1);
                    setImages([target, ...updated]);
                  }}
                  className="w-full h-7 text-[10px] font-black uppercase tracking-wider bg-white hover:bg-slate-50 text-slate-600 border rounded-lg transition"
                >
                  Set as Main Image
                </button>
              )}
            </div>
          ))}

          <div className="border border-slate-200 p-3 rounded-xl bg-slate-50/30 space-y-2">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
              Product Video
            </span>
            <MediaUploadComp
              label="Video Integration Asset Component"
              value={videoUrl}
              onChange={setVideoUrl}
              type="video"
              onFileSelect={handleVideoFileChange}
            />
          </div>
        </div>
      </div>

      {/* SPOTLIGHT HIGHLIGHTS, INGREDIENTS & SPECIFICATIONS */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-3xs space-y-5">
        <p className="text-slate-900 text-sm font-black uppercase tracking-wide border-b pb-1.5 border-slate-100">
          Key Highlights & Detailed Specifications
        </p>

        {/* Feature Highlights */}
        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-600 block">
            Bullet Feature Highlights
          </label>
          {features.map((feat, index) => (
            <div key={index} className="flex gap-2 items-center">
              <div className="flex-1">
                <TextInputComp
                  value={feat}
                  onChangeText={(val) => {
                    const updated = [...features];
                    updated[index] = val;
                    setFeatures(updated);
                  }}
                  label=""
                  placeholder="Enter key feature sentence..."
                />
              </div>
              {features.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setFeatures(features.filter((_, idx) => idx !== index))
                  }
                  className="text-rose-500 hover:text-rose-700 transition"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setFeatures([...features, ""])}
            className="text-[10px] font-black uppercase tracking-wider text-[#2F5E2E] flex items-center gap-1 mt-1 hover:underline"
          >
            Add Highlight Row
          </button>
        </div>

        {/* Specifications Data Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t pt-4 border-slate-100">
          <TextAreaComp
            value={ingredients}
            onChangeText={setIngredients}
            label="Ingredients"
            showLabel
            placeholder="List ingredients composition details..."
          />
          <TextAreaComp
            value={instruction}
            onChangeText={setInstruction}
            label="Handling & Usage Instructions"
            showLabel
            placeholder="Storage temperature, feeding tips..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
          <div className="space-y-1">
            <label className="text-xs font-bold text-slate-600 block">
              Manufacture Date
            </label>
            <input
              type="date"
              value={manufactureDate}
              onChange={(e) => setManufactureDate(e.target.value)}
              className="w-full h-9 text-xs px-3 border border-slate-200 bg-white rounded-xl focus:border-[#2F5E2E] text-slate-700 font-medium"
            />
          </div>
        </div>

        {/* Marketing Blocks */}
        <div className="space-y-3 border-t pt-4 border-slate-100">
          <p className="text-xs font-bold text-slate-800 uppercase tracking-wide">
            Marketing Copy Blocks (~50 words each)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextAreaComp
              value={marketingText1}
              onChangeText={setMarketingText1}
              label="Marketing Text 1"
              showLabel
              placeholder="Primary promo copy block..."
            />
            <TextAreaComp
              value={marketingText2}
              onChangeText={setMarketingText2}
              label="Marketing Text 2"
              showLabel
              placeholder="Secondary promotional highlights..."
            />
            <TextAreaComp
              value={marketingText3}
              onChangeText={setMarketingText3}
              label="Marketing Text 3"
              showLabel
              placeholder="Additional product story text..."
            />
            <TextAreaComp
              value={marketingText4}
              onChangeText={setMarketingText4}
              label="Marketing Text 4"
              showLabel
              placeholder="Footnote/guarantee marketing points..."
            />
          </div>
        </div>
      </div>

      {/* FORM FOOTER BUTTON ACTIONS */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <CommonButton
          text="Cancel"
          variant="outline"
          rounded="lg"
          onClick={() => navigate(-1)}
          disabled={isSubmitting || isUploadingFile}
        />
        <CommonButton
          text={isSubmitting ? "Saving..." : "Save Product"}
          variant="filled"
          rounded="lg"
          className="bg-[#2F5E2E] hover:bg-[#1a3a19] text-white font-black text-xs px-6 py-2.5 shadow-sm"
          disabled={isSubmitting || isUploadingFile}
        />
      </div>

      {/* Crop Modal */}
      {cropModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl p-5 max-w-2xl w-full max-h-[90vh] flex flex-col space-y-4 shadow-2xl border border-slate-100">
            <div className="flex items-center justify-between border-b pb-2 border-slate-100">
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                Crop Image Selection
              </h3>
              <button
                type="button"
                onClick={() => setCropModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 flex items-center justify-center bg-slate-950/5 p-4 rounded-xl border border-slate-200 min-h-[300px] max-h-[60vh] overflow-hidden">
              {cropModalSrc && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  className="max-h-[55vh] flex items-center justify-center"
                >
                  <img
                    src={cropModalSrc}
                    onLoad={onImageLoad}
                    alt="Source to crop"
                    className="max-h-[55vh] max-w-full w-auto h-auto object-contain block mx-auto"
                  />
                </ReactCrop>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setCropModalOpen(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyCrop}
                disabled={isUploadingFile}
                className="px-5 py-2 text-xs font-black uppercase tracking-wider text-white bg-[#2F5E2E] hover:bg-[#1a3a19] rounded-xl flex items-center gap-1.5 shadow-sm disabled:opacity-50 transition"
              >
                {isUploadingFile ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : null}
                {isUploadingFile ? "Uploading..." : "Apply & Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default AdminProductAddScreen;